import type { AreaFeature, AreaGeoJSON } from './types';

interface LatLng { lat: number; lng: number; }

/**
 * Finds the district name containing the given lat/lng point, or null if none matched.
 * When a point falls within multiple overlapping features (e.g. an enclave city surrounded
 * by a larger city), the feature with the smallest bounding area is returned so that the
 * most-specific (innermost) match wins.
 */
export function findDistrictAtPoint(lat: number, lng: number, geoJsonData: AreaGeoJSON): string | null {
    const point: LatLng = { lat, lng };

    let bestFeature: AreaFeature | null = null;
    let bestArea = Infinity;

    for (const feature of geoJsonData.features) {
        if (isPointInFeature(point, feature)) {
            const area = getFeatureBoundingArea(feature);
            if (area < bestArea) {
                bestArea = area;
                bestFeature = feature;
            }
        }
    }

    return bestFeature ? bestFeature.properties.Name : null;
}

/** Returns the bounding box area of a feature as a simple heuristic for feature size. */
export function getFeatureBoundingArea(feature: AreaFeature): number {
    const coords = getAllCoordinates(feature);
    if (coords.length === 0) return Infinity;

    let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
    for (const [lng, lat] of coords) {
        if (lng < minLng) minLng = lng;
        if (lng > maxLng) maxLng = lng;
        if (lat < minLat) minLat = lat;
        if (lat > maxLat) maxLat = lat;
    }
    return (maxLng - minLng) * (maxLat - minLat);
}

/** Flattens all coordinate pairs from a Polygon or MultiPolygon feature. */
function getAllCoordinates(feature: AreaFeature): number[][] {
    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
        return (geometry.coordinates as number[][][]).flat();
    } else if (geometry.type === 'MultiPolygon') {
        return (geometry.coordinates as number[][][][]).flat(2);
    }
    return [];
}

/**
 * Determines if a point falls within a GeoJSON district feature.
 * Supports both Polygon and MultiPolygon geometry types.
 */
export function isPointInFeature(point: LatLng, feature: AreaFeature): boolean {
    const geometry = feature.geometry;
    if (geometry.type === 'Polygon') {
        return isPointInPolygon(point, geometry.coordinates as number[][][]);
    } else if (geometry.type === 'MultiPolygon') {
        return (geometry.coordinates as number[][][][]).some(
            polygon => isPointInPolygon(point, polygon)
        );
    }
    return false;
}

/** Ray-casting algorithm for accurate point-in-polygon detection */
function isPointInPolygon(point: LatLng, rings: number[][][]): boolean {
    // Check outer ring
    if (!rayCast(point.lat, point.lng, rings[0])) {
        return false;
    }
    // Check holes — point must not be inside any hole
    for (let i = 1; i < rings.length; i++) {
        if (rayCast(point.lat, point.lng, rings[i])) {
            return false;
        }
    }
    return true;
}

/** Returns true if the point is inside the ring using the ray-casting method */
function rayCast(lat: number, lng: number, ring: number[][]): boolean {
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        // GeoJSON coordinates are [lng, lat]
        const xi = ring[i][1], yi = ring[i][0];
        const xj = ring[j][1], yj = ring[j][0];

        if (((yi > lng) !== (yj > lng)) &&
            (lat < (xj - xi) * (lng - yi) / (yj - yi) + xi)) {
            inside = !inside;
        }
    }
    return inside;
}
